import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  createStyles,
  createTheme,
  makeStyles,
  MuiThemeProvider
} from '@material-ui/core';
import { FormValues } from 'components/Forms';
import { useDispatch } from 'react-redux';
import { getDomainSlice } from 'store';
import { appointmentDomain, jobTypeDomain } from 'models';
import {
  AppointmentCategory,
  Brand,
  CreateCustomerAppointmentRequest,
  CustomerAppointmentType,
  EVBooking,
  EVFormChannel,
  EvIsolatorBooking,
  FuelType,
  MaintenanceBooking,
  NoteType,
  PaymentType,
  Smets2Booking
} from 'models/Appointment';
import { DateTime } from 'luxon';
import Modal, { useModalStyles } from 'components/Modal';
import { FormApiWithRestart } from 'components/Forms/Form';
import { SecondaryButton, SubmitButton } from 'components/Forms/Buttons';
import defaultTheme, { modalFormInputOverrides } from 'styles/defaultTheme';
import { useAllJobTypesData, useAvailabilityData } from 'store/selectors';
import { commonErrorHandling } from 'store/utils';
import { fetchAvailability } from 'store/slices/availability';
import InitialForm from './InitialForm';
import JobForm from './JobForm';
import Summary from './Summary';
import {
  VulnerabilitiesFieldNames,
  jumptechUrlRegex,
  JobTypeData,
  JobFormFieldName,
  DispatchDay
} from './JobFormConfig';

export enum FormStep {
  INITIAL,
  JOB,
  SUMMARY
}

interface AddJobModalProps {
  open: boolean;
  closeModal: () => void;
}

export const useAddJobModalStyles = makeStyles(theme => {
  return createStyles<string, {}>({
    dialogContent: {
      paddingBottom: 0,
      borderTop: `1px solid ${theme.palette.divider}`,
      '&::-webkit-scrollbar': {
        width: 0
      }
    },
    infoContainer: {
      backgroundColor: theme.palette.aegis.semantic.background.canvas,
      padding: theme.spacing(2, 3),
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(-3),
      marginRight: theme.spacing(-3)
    },
    info: {
      '& > [class*="MuiTypography-root"]:first-child': {
        marginRight: theme.spacing(1)
      },
      '& > [class*="MuiTypography-root"]:nth-child(2)': {
        color: theme.palette.aegis.semantic.message.dark
      },
      '&:last-child': {
        marginBottom: 0,
        paddingBottom: 0,
        borderBottom: 'none'
      },
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
      paddingBottom: theme.spacing(0.5),
      borderBottom: `1px solid ${theme.palette.aegis.semantic.border.dark}`
    },
    infoWrap: {
      flexWrap: 'wrap' as const,
      '& > [class*="MuiTypography-root"]:first-child': {
        flexBasis: '100%',
        margin: theme.spacing(0, 0, 0.5, 0)
      }
    },
    formSectionHeading: {
      margin: theme.spacing(5, 0, 2, 0)
    }
  });
});

const getOptionalFieldValue = (value: string) => value || null;

const defaultFormValues: FormValues = {
  [JobFormFieldName.ImmediateDispatch]: false,
  [JobFormFieldName.ImmediateDispatchDay]: DispatchDay.Today
};

export const parseFormValues = (
  values: FormValues
): CreateCustomerAppointmentRequest => {
  const jobType = values.jobType as { id: string; name: string };
  const slot = {
    startTime: (values.timeSlot as string).split('-')[0].trim(),
    endTime: (values.timeSlot as string).split('-')[1].trim(),
    date: DateTime.fromISO(values.date as string).toISODate()
  };

  const address = {
    addressLineOne: values.addressLine1 as string,
    addressLineTwo: getOptionalFieldValue(values.addressLine2 as string),
    addressLineThree: getOptionalFieldValue(values.addressLine3 as string),
    city: values.city as string,
    postcode: (values.postcode as string).trim()
  };

  const customerDetails = {
    firstName: values.firstName as string,
    lastName: values.lastName as string,
    primaryPhoneNumber: values.phoneNumber as string,
    secondaryPhoneNumber: getOptionalFieldValue(
      values.secondaryNumber as string
    )
  };

  const appointmentType:
    | EVBooking
    | EvIsolatorBooking
    | Smets2Booking
    | MaintenanceBooking = (() => {
    switch (values.appointmentType) {
      case CustomerAppointmentType.EV:
        return {
          type: CustomerAppointmentType.EV,
          details: {
            channel: values.channel as EVFormChannel,
            jumptechProjectId: (values.jumptechURL as string)?.match(
              jumptechUrlRegex
            )?.groups?.jumptechProjectId,
            zohoAccountId: values.zohoAccountId as string,
            mpan: values.mpan as string
          }
        };
      case CustomerAppointmentType.EV_ISOLATOR:
        return {
          type: CustomerAppointmentType.EV_ISOLATOR,
          details: {
            channel: values.channel as EVFormChannel,
            elecMsn: values.electricityMsn as string,
            mpan: values.mpan as string
          }
        };
      case CustomerAppointmentType.MAINTENANCE:
        return {
          type: CustomerAppointmentType.MAINTENANCE,
          paymentType: values.paymentType as PaymentType,
          fuelType: values.fuelType as FuelType,
          details: {
            mprn: values.mprn as string,
            mpan: values.mpan as string,
            oldGasMsn: values.gasMsn as string,
            oldElecMsn: values.electricityMsn as string
          }
        };
      case CustomerAppointmentType.SMETS2:
      default:
        return {
          type: CustomerAppointmentType.SMETS2,
          fuelType: values.fuelType as FuelType,
          paymentType: values.paymentType as PaymentType,
          details: {
            mprn: values.mprn as string,
            mpan: values.mpan as string,
            meterTechnicalDetails: {
              oldGasMsn: values.gasMsn as string,
              oldElecMsn: values.electricityMsn as string
            }
          }
        };
    }
  })();

  const getVulnerabilities = () => {
    const fieldsToExclude: string[] = [
      VulnerabilitiesFieldNames.CustomerHasVulnerabilities,
      VulnerabilitiesFieldNames.HasOtherVulnerability
    ];

    const vulnerabilitiesEntries = Object.values(VulnerabilitiesFieldNames)
      .filter(fieldName => !fieldsToExclude.includes(fieldName))
      .map(fieldName => {
        const formValue =
          fieldName === VulnerabilitiesFieldNames.OtherVulnerability
            ? values[fieldName] ?? null
            : values[fieldName] ?? false;
        return [fieldName, formValue];
      });

    return Object.fromEntries(vulnerabilitiesEntries);
  };

  const notes = values.bookingNotes
    ? [{ note: values.bookingNotes as string, type: NoteType.SCHEDULER }]
    : [];

  return {
    brand: Brand.OVO,
    category: AppointmentCategory.CUSTOMER,
    jobType: jobType.id,
    appointmentType,
    address,
    accountNumber: values.accountNumber as string | undefined,
    slot,
    notes,
    customerDetails: {
      ...customerDetails,
      vulnerability:
        values[VulnerabilitiesFieldNames.CustomerHasVulnerabilities] === 'true'
          ? getVulnerabilities()
          : {}
    }
  };
};

interface AddJobModalActionsProps {
  formStep: FormStep;
  setFormStep: Dispatch<SetStateAction<FormStep>>;
  formRef: MutableRefObject<FormApiWithRestart | undefined>;
  closeModal: () => void;
  resetForm: () => void;
  formValues: FormValues;
}

export function AddJobModalActions({
  formStep,
  setFormStep,
  formRef,
  closeModal,
  resetForm,
  formValues
}: AddJobModalActionsProps) {
  const dispatch = useDispatch();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const { actions: appointmentActions } = getDomainSlice(appointmentDomain);
  const classes = useModalStyles();

  const clearFormState = () => {
    setFormStep(FormStep.INITIAL);
    resetForm();
  };

  const submitAddJobForm = async () => {
    setIsSubmitDisabled(true);

    try {
      const isSuccessfulRequest = await dispatch<boolean>(
        appointmentActions.custom?.createJob(
          parseFormValues(formValues),
          formValues[JobFormFieldName.ImmediateDispatch]
        )
      );

      setIsSubmitDisabled(false);

      if (isSuccessfulRequest) {
        closeModal();
        clearFormState();
      }
    } catch (e) {
      commonErrorHandling(e);
    }
  };

  if (formStep === FormStep.INITIAL) {
    return (
      <>
        <SecondaryButton onClick={() => closeModal()} testId="addJobFormCancel">
          Cancel
        </SecondaryButton>
        <SubmitButton
          onClick={() => formRef.current?.submit()}
          testId="addJobFormNext"
        >
          Next
        </SubmitButton>
      </>
    );
  }
  if (formStep === FormStep.JOB) {
    return (
      <>
        <SecondaryButton
          onClick={() => setFormStep(FormStep.INITIAL)}
          testId="addJobFormBack"
          className={classes.previousButton}
        >
          Previous
        </SecondaryButton>
        <SecondaryButton
          onClick={() => clearFormState()}
          testId="addJobFormCancel"
        >
          Cancel
        </SecondaryButton>
        <SubmitButton
          onClick={() => formRef.current?.submit()}
          testId="addJobFormNext"
        >
          Next
        </SubmitButton>
      </>
    );
  }

  return (
    <>
      <SecondaryButton
        onClick={() => setFormStep(FormStep.JOB)}
        testId="addJobFormBack"
        className={classes.previousButton}
      >
        Previous
      </SecondaryButton>
      <SecondaryButton
        onClick={() => clearFormState()}
        testId="addJobFormCancel"
      >
        Cancel
      </SecondaryButton>
      <SubmitButton
        onClick={submitAddJobForm}
        testId="addJobFormSubmit"
        disabled={isSubmitDisabled}
      >
        Save
      </SubmitButton>
    </>
  );
}

export default function AddJobModal({ open, closeModal }: AddJobModalProps) {
  const dispatch = useDispatch();
  const { actions } = getDomainSlice(jobTypeDomain);
  const jobTypeData = useAllJobTypesData();
  const availabilityData = useAvailabilityData();

  const [formStep, setFormStep] = useState(FormStep.INITIAL);

  // This setup is needed in order to catch the event after a successful availability request
  const [isFormStepSubmitting, setIsFormStepSubmitting] = useState(false);
  const [formStepSuccess, setFormStepSuccess] = useState(false);
  const [selectedDate, setSelectedDate] = useState(DateTime.now());

  const [postCode, setPostcode] = useState('');
  const [jobType, setJobType] = useState<JobTypeData>({
    id: null,
    name: null,
    extraAttributes: null
  });

  const [jobFormValues, setJobFormValues] = useState<FormValues>(
    defaultFormValues
  );
  const formRef = useRef<FormApiWithRestart>();

  useEffect(() => {
    if (formStepSuccess && !isFormStepSubmitting) {
      if (availabilityData?.length) {
        setFormStep(FormStep.JOB);
      }
    }
  }, [formStepSuccess, isFormStepSubmitting, availabilityData]);

  useEffect(() => {
    if (actions.get.listAll) {
      dispatch(actions.get.listAll());
    }
  }, []);

  // FormValues is acting weird so that's why the type here is any.
  // FormValues type and usage to be addressed in the future.
  const loadInitialDataModal = (data: any) => {
    const currentJobType = jobTypeData?.find((item: { id: string }) => {
      const [jobTypeName] = data.job_type?.values ?? [data.jobType];
      return item.id === jobTypeName;
    }) ?? { id: null, name: null, extraAttributes: null };

    const [postcode] = data.postcode.values ?? [data.postcode];

    setPostcode(postcode);
    setJobType(currentJobType);
  };

  const availabilityApiCallback = (formData: FormValues) => {
    loadInitialDataModal(formData);

    return dispatch(
      fetchAvailability(formData.postcode as string, formData.jobType as string)
    );
  };

  const resetForm = () => {
    setJobFormValues(defaultFormValues);
    setPostcode('');
    setJobType({ id: null, name: null, extraAttributes: null });
  };

  const saveJobFormData = (fieldValues: FormValues) => {
    const filteredFieldValues: FormValues = {
      ...fieldValues,
      fuelType:
        jobType?.extraAttributes?.fuelType ??
        (fieldValues.fuelType as FuelType),
      appointmentType:
        jobType?.extraAttributes?.bookingType ??
        (fieldValues.appointmentType as CustomerAppointmentType),
      ...(fieldValues[JobFormFieldName.ImmediateDispatch]
        ? {
            date: DateTime.now().plus({
              days:
                fieldValues[JobFormFieldName.ImmediateDispatchDay] ===
                DispatchDay.Tomorrow
                  ? 1
                  : 0
            })
          }
        : {})
    };
    setJobFormValues(filteredFieldValues);
    setFormStep(FormStep.SUMMARY);
  };

  return (
    <Modal
      title="New Job"
      testId="addJobModal"
      open={open}
      onClose={closeModal}
      modalActions={
        <AddJobModalActions
          formStep={formStep}
          setFormStep={setFormStep}
          resetForm={resetForm}
          closeModal={closeModal}
          formRef={formRef}
          formValues={jobFormValues}
        />
      }
    >
      <MuiThemeProvider
        theme={createTheme({
          ...defaultTheme,
          overrides: { ...defaultTheme.overrides, ...modalFormInputOverrides }
        })}
      >
        {formStep === FormStep.INITIAL && (
          <InitialForm
            jobTypeData={jobTypeData || []}
            initialValues={{ postcode: postCode, jobType: jobType.id || '' }}
            availabilityApiCallback={availabilityApiCallback}
            setFormStepSuccess={setFormStepSuccess}
            setIsFormStepSubmitting={setIsFormStepSubmitting}
            formRef={formRef}
          />
        )}
        {formStep === FormStep.JOB && (
          <JobForm
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            postCode={postCode}
            jobTypeData={jobType}
            initialValues={jobFormValues}
            saveJobData={saveJobFormData}
            setFormStepSuccess={setFormStepSuccess}
            setIsFormStepSubmitting={setIsFormStepSubmitting}
            formRef={formRef}
          />
        )}
        {formStep === FormStep.SUMMARY && (
          <Summary appointment={jobFormValues as any} formRef={formRef} />
        )}
      </MuiThemeProvider>
    </Modal>
  );
}
